/** Shared production-grade validation helpers for admin forms */

export const trimValue = (value) =>
  typeof value === "string" ? value.trim() : value;

export const isRequired = (value, label = "This field") => {
  if (value === null || value === undefined) return `${label} is required`;
  if (typeof value === "string" && !value.trim()) return `${label} is required`;
  return null;
};

export const isEmail = (value) => {
  if (!value) return "Email is required";
  const email = String(value).trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Enter a valid email address";
  return null;
};

export const isPhone = (value) => {
  if (!value) return "Phone number is required";
  const phone = String(value).replace(/\D/g, "");
  if (!/^[6-9]\d{9}$/.test(phone)) return "Enter a valid 10-digit Indian mobile number";
  return null;
};

export const isPassword = (value, { min = 8 } = {}) => {
  if (!value) return "Password is required";
  if (value.length < min) return `Password must be at least ${min} characters`;
  if (!/[A-Z]/.test(value)) return "Password must include an uppercase letter";
  if (!/[a-z]/.test(value)) return "Password must include a lowercase letter";
  if (!/[0-9]/.test(value)) return "Password must include a number";
  return null;
};

export const isAmount = (value, { min = 1, max = 10000000 } = {}) => {
  if (value === "" || value === null || value === undefined) return "Amount is required";
  const num = Number(value);
  if (Number.isNaN(num)) return "Enter a valid amount";
  if (num < min) return `Amount must be at least ₹${min}`;
  if (num > max) return `Amount cannot exceed ₹${max.toLocaleString("en-IN")}`;
  if (!/^\d+(\.\d{1,2})?$/.test(String(value))) return "Amount can have up to 2 decimal places";
  return null;
};

export const isIFSC = (value) => {
  if (!value) return "IFSC is required";
  if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(String(value).trim().toUpperCase())) {
    return "Enter a valid IFSC code";
  }
  return null;
};

export const isAccountNumber = (value) => {
  if (!value) return "Account number is required";
  if (!/^\d{9,18}$/.test(String(value).trim())) return "Account number must be 9–18 digits";
  return null;
};

export const isPAN = (value) => {
  if (!value) return "PAN is required";
  if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(String(value).trim().toUpperCase())) {
    return "Enter a valid PAN (e.g. ABCDE1234F)";
  }
  return null;
};

export const isAadhaar = (value) => {
  if (!value) return "Aadhaar is required";
  if (!/^\d{12}$/.test(String(value).replace(/\s/g, ""))) return "Aadhaar must be 12 digits";
  return null;
};

export const isGST = (value) => {
  if (!value) return "GSTIN is required";
  if (
    !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
      String(value).trim().toUpperCase()
    )
  ) {
    return "Enter a valid GSTIN";
  }
  return null;
};

export const isPincode = (value) => {
  if (!value) return "Pincode is required";
  if (!/^[1-9][0-9]{5}$/.test(String(value).trim())) return "Enter a valid 6-digit pincode";
  return null;
};

export const isMPIN = (value) => {
  if (!value) return "MPIN is required";
  if (!/^\d{6}$/.test(String(value))) return "MPIN must be exactly 6 digits";
  return null;
};

export const isUsername = (value) => {
  if (!value) return "Username is required";
  const v = String(value).trim();
  if (v.length < 3 || v.length > 30) return "Username must be 3–30 characters";
  if (!/^[A-Za-z0-9_]+$/.test(v)) return "Username can only contain letters, numbers, and _";
  return null;
};

export const sanitizeInput = (value) => {
  if (typeof value !== "string") return value;
  return value
    .trim()
    .replace(/[<>]/g, "")
    .replace(/javascript:/gi, "");
};

export const onlyDigits = (value, maxLength) => {
  const digits = String(value || "").replace(/\D/g, "");
  return maxLength ? digits.slice(0, maxLength) : digits;
};

export const onlyAlphaNumeric = (value) =>
  String(value || "").replace(/[^A-Za-z0-9]/g, "");

/**
 * Run a map of field validators. Returns { valid, errors }.
 * validatorsMap: { field: () => errorString|null }
 */
export const validateFields = (validatorsMap) => {
  const errors = {};
  Object.entries(validatorsMap).forEach(([field, fn]) => {
    const err = fn();
    if (err) errors[field] = err;
  });
  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
};

export const formatINR = (amount) => {
  const num = Number(amount) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(num);
};

export const formatCompactINR = (amount) => {
  const num = Number(amount) || 0;
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
  return formatINR(num);
};
